import React, { useEffect, useState } from "react";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import FormField from "@cloudscape-design/components/form-field";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Multiselect from "@cloudscape-design/components/multiselect";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Table from "@cloudscape-design/components/table";
import Tabs from "@cloudscape-design/components/tabs";
import Textarea from "@cloudscape-design/components/textarea";
import Toggle from "@cloudscape-design/components/toggle";
import { useRecoilValue } from "recoil";
import CohortConditionsEditor from "./CohortConditionsEditor";
import { WELCOME_REGISTRATION_COHORT_FIELDS } from "../helpers/cohortConditions";
import { userStore } from "../stores/access";
import { MAX_BANNER_BYTES } from "../stores/homeBannersAddModal";

const audienceOptions = [
	{ value: "guest", label: "Guest" },
	{ value: "registered", label: "Registered" },
	{ value: "all", label: "All" },
];

const textTypeOptions = ["local", "international", "mixed"].map((value) => ({ value, label: value }));

const actionTypeOptions = [
	{ label: "Internal", value: "internal" },
	{ label: "External", value: "external" },
];

const excludedContextOptions = [
	{ value: "wallet", label: "Wallet" },
	{ value: "otherModal", label: "Any other modal" },
	{ value: "game", label: "Games" },
	{ value: "authRegistration", label: "Authentication and registration" },
];

const welcomeRegistrationCohortFieldSet = new Set(WELCOME_REGISTRATION_COHORT_FIELDS);
const getAllowedCohortFields = (modalKey) => (modalKey === "welcome.banner" ? WELCOME_REGISTRATION_COHORT_FIELDS : undefined);
const sanitizeCohortConditions = (modalKey, conditions) => {
	const cohortConditions = Array.isArray(conditions) ? conditions : [];
	return modalKey === "welcome.banner" ? cohortConditions.filter((condition) => welcomeRegistrationCohortFieldSet.has(condition?.field)) : cohortConditions;
};

const normalizeExcludedPath = (value) => {
	const path = String(value || "").trim();
	if (!path || !path.startsWith("/") || path.startsWith("//") || /[\s?#]/.test(path)) return "";

	return path === "/" ? path : path.replace(/\/+$/, "");
};

const DATE_RANGE_I18N = {
	todayAriaLabel: "Today",
	nextMonthAriaLabel: "Next month",
	previousMonthAriaLabel: "Previous month",
	customRelativeRangeDurationLabel: "Duration",
	customRelativeRangeDurationPlaceholder: "Enter duration",
	customRelativeRangeOptionLabel: "Custom range",
	customRelativeRangeOptionDescription: "Set a custom range in the past",
	customRelativeRangeUnitLabel: "Unit",
	formatRelativeRange: (value) => value,
	formatUnit: (unit, value) => `${value} ${unit}`,
	dateTimeConstraintText: "Use YYYY-MM-DD.",
	relativeModeTitle: "Relative range",
	absoluteModeTitle: "Absolute range",
	relativeRangeSelectionHeading: "Choose a range",
	startDateLabel: "Start date",
	endDateLabel: "End date",
	startTimeLabel: "Start time",
	endTimeLabel: "End time",
	clearButtonLabel: "Clear",
	cancelButtonLabel: "Cancel",
	applyButtonLabel: "Apply",
};

const formatCalendarDate = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");

	return `${year}-${month}-${day}`;
};

const createDefaultStatisticsRange = () => {
	const end = new Date();
	const start = new Date(end);
	start.setDate(start.getDate() - 29);

	return {
		type: "absolute",
		startDate: formatCalendarDate(start),
		endDate: formatCalendarDate(end),
	};
};

const formatCount = (value) => (Number(value) || 0).toLocaleString("en-US");
const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const validateStatisticsRange = (range) => {
	if (range?.type !== "absolute" || !range.startDate || !range.endDate) {
		return { valid: false, errorMessage: "Select a valid date range" };
	}

	if (range.startDate > range.endDate) {
		return { valid: false, errorMessage: "Start date must not be after end date" };
	}

	return { valid: true };
};

const audienceLabel = (value) => audienceOptions.find((option) => option.value === value)?.label || "—";

const numberLabel = (value) => (value === null || value === undefined || value === "" ? "—" : String(value));

const draftFromModal = (modal) => ({
	key: modal.key,
	title: modal.title,
	presentation: modal.presentation || "modal",
	enabled: modal.enabled === true,
	audience: modal.audience || "",
	intervalDays: modal.intervalDays == null ? "" : String(modal.intervalDays),
	maxDismissals: modal.maxDismissals == null ? "" : String(modal.maxDismissals),
	buttonText: modal.buttonText || "",
	buttonTextType: modal.buttonTextType || "local",
	buttonActionLocked: modal.buttonActionLocked === true,
	contentLocked: modal.contentLocked === true,
	audienceLocked: modal.audienceLocked === true,
	buttonActionType: modal.buttonActionType || "",
	buttonAction: modal.buttonAction || "",
	image: modal.image || "",
	priority: modal.priority == null ? "" : String(modal.priority),
	excludedContexts: Array.isArray(modal.excludedContexts) ? modal.excludedContexts : [],
	excludedPathsText: Array.isArray(modal.excludedPaths) ? modal.excludedPaths.join("\n") : "",
	cohortConditions: Array.isArray(modal.cohortConditions) ? modal.cohortConditions : [],
});

function ModalOfferEditor({ Interface, draft, saving, onDismiss, onSave }) {
	const [form, setForm] = useState(draft);
	const patch = (values) => setForm((current) => ({ ...current, ...values }));
	const [activeTabId, setActiveTabId] = useState("settings");
	const selectedAudience = audienceOptions.find((option) => option.value === form.audience) || null;
	const selectedActionType = actionTypeOptions.find((option) => option.value === form.buttonActionType) || null;
	const selectedTextType = textTypeOptions.find((option) => option.value === form.buttonTextType) || textTypeOptions[0];
	const isNavigationBanner = form.presentation === "navigationBanner";
	const selectedExcludedContexts = excludedContextOptions.filter((option) => form.excludedContexts.includes(option.value));
	const cohortsEnabled = form.audience === "registered" || form.audience === "all";

	useEffect(() => {
		if (!cohortsEnabled && activeTabId === "cohorts") setActiveTabId("settings");
	}, [cohortsEnabled, activeTabId]);

	return (
		<Modal
			visible
			size="large"
			header={form.title}
			onDismiss={onDismiss}
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="s">
						<Button onClick={onDismiss}>Close</Button>
						<Button variant="primary" loading={saving} onClick={() => onSave(form)}>
							Save
						</Button>
					</SpaceBetween>
				</Box>
			}>
			<Tabs
				activeTabId={activeTabId}
				onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
				tabs={[
					{
						id: "settings",
						label: "Settings",
						content: (
							<SpaceBetween direction="vertical" size="m">
								<Toggle checked={form.enabled} onChange={({ detail }) => patch({ enabled: detail.checked })}>
									Enabled
								</Toggle>
								<FormField label="Audience" stretch>
									<Select
										stretch
										disabled={form.audienceLocked}
										placeholder="Select audience"
										selectedOption={selectedAudience}
										options={audienceOptions}
										onChange={({ detail }) => {
											const audience = detail.selectedOption.value;
											patch({
												audience,
												cohortConditions: audience === "guest" ? [] : form.cohortConditions,
											});
										}}
									/>
								</FormField>
								{!isNavigationBanner && !form.contentLocked ? (
									<FormField label="Image" stretch>
										<input
											type="file"
											accept="image/png"
											onChange={(event) => {
												const file = event.target.files[0];
												if (!file) return;
												if (file.size > MAX_BANNER_BYTES) {
													Interface.addNoty("Error", "Image size must not exceed 1 MB");
													event.target.value = "";
													return;
												}
												const reader = new FileReader();
												reader.onload = ({ target }) => patch({ image: target.result });
												reader.readAsDataURL(file);
											}}
										/>
										{form.image ? (
											<Box margin={{ top: "s" }}>
												<img src={form.image} alt="" style={{ maxWidth: "100%", maxHeight: "240px" }} />
											</Box>
										) : null}
									</FormField>
								) : null}
								{!isNavigationBanner && !form.contentLocked ? (
									<FormField label="Button text type" stretch>
										<Select stretch selectedOption={selectedTextType} options={textTypeOptions} onChange={({ detail }) => patch({ buttonTextType: detail.selectedOption.value })} />
									</FormField>
								) : null}
								{!isNavigationBanner && !form.contentLocked ? (
									<FormField label="Button text" stretch>
										<Input stretch value={form.buttonText} onChange={({ detail }) => patch({ buttonText: detail.value })} />
									</FormField>
								) : null}
								{isNavigationBanner ? (
									<FormField label="Download URL" description="Full HTTPS URL. Opens in a new tab." stretch>
										<Input value={form.buttonAction} onChange={({ detail }) => patch({ buttonAction: detail.value })} />
									</FormField>
								) : form.contentLocked ? null : form.buttonActionLocked ? (
									<FormField label="Button action" description="Always opens the PWA page. This cannot be changed." stretch>
										<Input disabled value="PWA page" />
									</FormField>
								) : (
									<>
										<FormField label="Button action" stretch>
											<Select placeholder="Select link type" selectedOption={selectedActionType} options={actionTypeOptions} onChange={({ detail }) => patch({ buttonActionType: detail.selectedOption.value })} />
										</FormField>
										<FormField label={form.buttonActionType === "external" ? "External URL" : "Internal path"} description={form.buttonActionType === "external" ? "Full http or https URL. Opens in a new tab." : "Site path, for example /wallet."} stretch>
											<Input value={form.buttonAction} onChange={({ detail }) => patch({ buttonAction: detail.value })} />
										</FormField>
									</>
								)}
								{!isNavigationBanner ? (
									<>
										<FormField label="Show again after, days" description="Whole number of days. Required." stretch>
											<Input stretch type="number" inputMode="numeric" value={form.intervalDays} onChange={({ detail }) => patch({ intervalDays: detail.value })} />
										</FormField>
										<FormField label="Max closes" description="Leave empty for no limit." stretch>
											<Input stretch type="number" inputMode="numeric" value={form.maxDismissals} onChange={({ detail }) => patch({ maxDismissals: detail.value })} />
										</FormField>
										<FormField label="Priority" description="Higher priority offers are shown first." stretch>
											<Input stretch type="number" inputMode="numeric" value={form.priority} onChange={({ detail }) => patch({ priority: detail.value })} />
										</FormField>
										<FormField label="Do not show in" description="Runtime contexts in which this offer stays deferred." stretch>
											<Multiselect selectedOptions={selectedExcludedContexts} options={excludedContextOptions} placeholder="Select contexts" selectedAriaLabel="Selected contexts" onChange={({ detail }) => patch({ excludedContexts: detail.selectedOptions.map((option) => option.value) })} />
										</FormField>
										<FormField label="Do not show on paths" description="One internal path per line. A path also matches its nested routes." stretch>
											<Textarea value={form.excludedPathsText} placeholder={"/game\n/promo/special"} onChange={({ detail }) => patch({ excludedPathsText: detail.value })} />
										</FormField>
									</>
								) : null}
							</SpaceBetween>
						),
					},
					{
						id: "cohorts",
						label: "Cohorts",
						disabled: !cohortsEnabled,
						disabledReason: "Cohorts apply to registered users. Choose Registered or All.",
						content: (
							<CohortConditionsEditor
								key={`${form.key}-${form.audience}`}
								conditions={form.cohortConditions}
								allowedFields={getAllowedCohortFields(form.key)}
								onChange={(cohortConditions) => patch({ cohortConditions })}
							/>
						),
					},
				]}
			/>
		</Modal>
	);
}

const isValidButtonAction = (form) => {
	const action = String(form.buttonAction || "").trim();
	if (form.presentation === "navigationBanner") {
		try {
			return new URL(action).protocol === "https:";
		} catch (error) {
			return false;
		}
	}
	if (form.contentLocked || form.buttonActionLocked || form.key === "install.pwa") return true;

	if (form.buttonActionType === "internal") {
		return action.startsWith("/") && !action.startsWith("//") && !/\s/.test(action);
	}

	if (form.buttonActionType !== "external") return false;

	try {
		const url = new URL(action);
		return url.protocol === "http:" || url.protocol === "https:";
	} catch (error) {
		return false;
	}
};

export default function ModalOffers({ Interface }) {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [draft, setDraft] = useState(null);
	const [activeTabId, setActiveTabId] = useState("management");
	const [statisticsRange, setStatisticsRange] = useState(createDefaultStatisticsRange);
	const [statistics, setStatistics] = useState([]);
	const [statisticsLoading, setStatisticsLoading] = useState(false);
	const [statisticsLoaded, setStatisticsLoaded] = useState(false);
	const [statisticsError, setStatisticsError] = useState("");

	const load = () => {
		setLoading(true);
		setError("");
		return Interface.request("get", { class: "data", module: "offerModals", method: "list" }, {})
			.then((response) => {
				setItems(Array.isArray(response?.data) ? response.data : []);
				setLoading(false);
			})
			.catch((requestError) => {
				setLoading(false);
				setError(requestError?.text || requestError?.message || "Couldn't load modals");
			});
	};

	useEffect(() => {
		load();
	}, []);

	const loadStatistics = (range = statisticsRange) => {
		const validation = validateStatisticsRange(range);

		if (!validation.valid) {
			setStatisticsError(validation.errorMessage);
			return Promise.resolve();
		}

		setStatisticsLoading(true);
		setStatisticsError("");

		return Interface.request("get", { class: "data", module: "offerModals", method: "statistics" }, { dateFrom: range.startDate, dateTo: range.endDate })
			.then((response) => {
				setStatistics(Array.isArray(response?.items) ? response.items : []);
				setStatisticsLoaded(true);
				setStatisticsLoading(false);
			})
			.catch((requestError) => {
				setStatisticsLoading(false);
				setStatisticsError(requestError?.text || requestError?.message || "Couldn't load modal offer statistics");
			});
	};

	const resetStatistics = () => {
		const range = createDefaultStatisticsRange();
		setStatisticsRange(range);
		loadStatistics(range);
	};

	const changeTab = (activeTabId) => {
		setActiveTabId(activeTabId);

		if (activeTabId === "statistics" && !statisticsLoaded && !statisticsLoading) loadStatistics();
	};

	const save = (form) => {
		const isNavigationBanner = form.presentation === "navigationBanner";
		const intervalDays = Number(form.intervalDays);
		const maxDismissals = form.maxDismissals === "" ? null : Number(form.maxDismissals);
		const priority = Number(form.priority);
		const rawExcludedPaths = form.excludedPathsText
			.split("\n")
			.map((path) => path.trim())
			.filter(Boolean);
		const excludedPaths = rawExcludedPaths.map(normalizeExcludedPath);

		if (!form.audience) {
			Interface.addNoty("Error", "Audience is required");
			return;
		}
		if (!isNavigationBanner && (!Number.isInteger(intervalDays) || intervalDays < 1)) {
			Interface.addNoty("Error", "Interval must be a whole number of days");
			return;
		}
		if (!isNavigationBanner && maxDismissals !== null && (!Number.isInteger(maxDismissals) || maxDismissals < 1)) {
			Interface.addNoty("Error", "Max closes must be empty or a whole number");
			return;
		}
		if (!isNavigationBanner && (!Number.isInteger(priority) || priority < 0)) {
			Interface.addNoty("Error", "Priority must be a non-negative whole number");
			return;
		}
		if (!isNavigationBanner && excludedPaths.some((path) => !path)) {
			Interface.addNoty("Error", "Excluded paths must be internal paths without query, hash or spaces");
			return;
		}
		if (!isValidButtonAction(form)) {
			const message = isNavigationBanner ? "Download URL must be a valid HTTPS URL" : form.buttonActionType === "external" ? "External link must be an http or https URL" : form.buttonActionType === "internal" ? "Internal link must start with /" : "Button action is required";
			Interface.addNoty("Error", message);
			return;
		}

		const payload = {
			key: form.key,
			enabled: form.enabled,
			audience: form.audience,
			cohortConditions: form.audience === "guest" ? [] : sanitizeCohortConditions(form.key, form.cohortConditions),
		};

		if (isNavigationBanner) {
			payload.buttonAction = form.buttonAction.trim();
		} else {
			payload.intervalDays = intervalDays;
			payload.maxDismissals = maxDismissals;
			payload.priority = priority;
			payload.excludedContexts = form.excludedContexts;
			payload.excludedPaths = [...new Set(excludedPaths)];

			if (!form.contentLocked) {
				payload.buttonText = form.buttonText;
				payload.buttonTextType = form.buttonTextType;
				payload.image = form.image;
			}

			if (!form.contentLocked && !form.buttonActionLocked && form.key !== "install.pwa") {
				payload.buttonActionType = form.buttonActionType;
				payload.buttonAction = form.buttonAction.trim();
			}
		}

		setSaving(true);
		Interface.request("set", { class: "data", module: "offerModals", method: "save" }, payload)
			.then(() => {
				Interface.addNoty("Success", `${form.title} saved`);
				setDraft(null);
				setSaving(false);
				return load();
			})
			.catch(() => {
				setSaving(false);
			});
	};

	return (
		<>
			<Tabs
				activeTabId={activeTabId}
				onChange={({ detail }) => changeTab(detail.activeTabId)}
				tabs={[
					{
						id: "management",
						label: "Management",
						content: (
							<SpaceBetween direction="vertical" size="m">
								{error ? (
									<Alert type="error" header="Couldn't load modal offers">
										{error}
									</Alert>
								) : null}
								<Table
									loading={loading}
									loadingText="Loading modal offers"
									items={items}
									trackBy="key"
									variant="embedded"
									empty={loading ? <Spinner /> : "No modal offers"}
									header={<Header variant="h1">Modal offers</Header>}
									columnDefinitions={[
										{ id: "title", header: "Offer", cell: (item) => item.title },
										{ id: "status", header: "Status", cell: (item) => (item.configured ? (item.enabled ? "On" : "Off") : "Not set") },
										{ id: "audience", header: "Audience", cell: (item) => audienceLabel(item.audience) },
										{ id: "interval", header: "Interval, days", cell: (item) => numberLabel(item.intervalDays) },
										{ id: "closes", header: "Max closes", cell: (item) => numberLabel(item.maxDismissals) },
										{ id: "priority", header: "Priority", cell: (item) => numberLabel(item.priority) },
										{
											id: "actions",
											header: "Control",
											cell: (item) => <Button onClick={() => setDraft(draftFromModal(item))}>Edit</Button>,
										},
									]}
								/>
							</SpaceBetween>
						),
					},
					{
						id: "statistics",
						label: "Statistics",
						content: (
							<SpaceBetween direction="vertical" size="m">
								{statisticsError ? (
									<Alert type="error" header="Couldn't load modal offer statistics">
										{statisticsError}
									</Alert>
								) : null}
								<SpaceBetween direction="horizontal" size="m">
									<FormField label="Period">
										<DateRangePicker value={statisticsRange} onChange={({ detail }) => setStatisticsRange(detail.value)} i18nStrings={DATE_RANGE_I18N} dateOnly isValidRange={validateStatisticsRange} placeholder="Select period" />
									</FormField>
									<Box padding={{ top: "xl" }}>
										<SpaceBetween direction="horizontal" size="s">
											<Button variant="primary" loading={statisticsLoading} onClick={() => loadStatistics()}>
												Apply
											</Button>
											<Button disabled={statisticsLoading} onClick={resetStatistics}>
												Reset
											</Button>
										</SpaceBetween>
									</Box>
								</SpaceBetween>
								<Table
									loading={statisticsLoading}
									loadingText="Loading modal offer statistics"
									items={statistics}
									trackBy="key"
									variant="embedded"
									empty={statisticsLoading ? <Spinner /> : "No modal offer statistics"}
									header={<Header variant="h1">Modal offers statistics</Header>}
									columnDefinitions={[
										{ id: "title", header: "Offer", cell: (item) => item.title },
										{ id: "viewsUnique", header: "Views (Unique)", cell: (item) => formatCount(item.viewsUnique) },
										{ id: "viewsTotal", header: "Views (Total)", cell: (item) => formatCount(item.viewsTotal) },
										{ id: "ctaClicks", header: "CTA Clicks", cell: (item) => formatCount(item.ctaClicks) },
										{ id: "ctr", header: "CTR", cell: (item) => formatPercent(item.ctr) },
										{ id: "ctrUnique", header: "CTR (Unique)", cell: (item) => formatPercent(item.ctrUnique) },
										{ id: "closeClicks", header: "Close Clicks", cell: (item) => formatCount(item.closeClicks) },
									]}
								/>
							</SpaceBetween>
						),
					},
				]}
			/>
			{draft ? <ModalOfferEditor Interface={Interface} draft={draft} saving={saving} onDismiss={() => setDraft(null)} onSave={save} /> : null}
		</>
	);
}
